import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsContext } from '../../contexts/SettingsContext';
// Import all data hooks directly to ensure real-time data
import { 
  useFinancialData, 
  useReservationsData, 
  useClientsData, 
  usePackagesData,
  useDocumentsData,
  useSuppliersData
} from '../../services/dataService';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Fade,
  Grow,
  Slide,
  ButtonBase,
  useTheme,
  useMediaQuery,
  Skeleton,
  IconButton,
  Stack,
  Tooltip,
  AvatarGroup,
  Badge,
  Zoom,
  CircularProgress,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  People,
  Flight,
  Euro,
  TrendingUp,
  CalendarToday,
  Notifications,
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Add,
  Visibility,
  Assessment,
  Business,
  Description,
  ArrowForward,
  AttachMoney,
  Today,
  Group,
  Star,
  Speed,
  Timeline,
  BarChart,
  PieChart,
  ShowChart,
  TrendingDown,
  AccountBalance,
  LocationOn,
  Public,
  LocalActivity,
  Favorite,
  BookmarkBorder,
  Brightness1,
  PlayArrow,
  MoreVert,
  FilterList,
  Refresh,
  CloudDone,
  Wifi,
  BatteryFull,
  SignalWifi4Bar,
  WbSunny,
  Thermostat,
  Air,
  MonetizationOn,
  FlightTakeoff,
  FlightLand,
  Hotel,
  Restaurant,
  DirectionsCar,
  CameraAlt,
  ShareLocation,
  Explore,
  Language,
  CardGiftcard,
  CompareArrows,
  Analytics,
  RocketLaunch,
  AutoAwesome,
  LocalFireDepartment,
  ElectricBolt,
  Diamond,
  Whatshot,
  Celebration
} from '@mui/icons-material';

// Custom Chart Components
// Replace AnimatedChart component to directly display data without animation or hover effects
const AnimatedChart = ({ data, color, height = 60 }) => {
  return (
    <Box 
      className="dashboard-chart-container"
      sx={{ 
        display: 'flex', 
        alignItems: 'end', 
        height, 
        gap: 0.5,
        opacity: 1
      }}
    >
      {data.map((value, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: `${(value / Math.max(...data)) * 100}%`,
            background: `linear-gradient(to top, ${color}80, ${color})`,
            borderRadius: '2px 2px 0 0'
          }}
        />
      ))}
    </Box>
  );
};

// Update text color in GlowingMetric to ensure visibility and remove animation
const GlowingMetric = ({ label, value, icon, color, trend, children }) => (
  <Box sx={{ 
    position: 'relative',
    p: 2,
    borderRadius: 3,
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${color}20, transparent)`,
      zIndex: 0
    }
  }}>
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ 
          p: 1,
          borderRadius: 2,
          background: `${color}20`,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        {trend && (
          <Chip 
            label={trend} 
            size="small" 
            sx={{ 
              backgroundColor: trend.startsWith('+') ? '#059669' : '#dc2626',
              color: 'white',
              fontWeight: 600
            }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'rgba(51,65,85,0.9)', mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(51,65,85,0.8)' }}>
        {label}
      </Typography>
      <Box className="dashboard-chart-container" sx={{ mt: 1.5 }}>
      {children}
      </Box>
    </Box>
  </Box>
);

// Fix FloatingActionCard to use a better contrasted background without hover effects
const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
  // Get theme and check if mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card sx={{
    background: `linear-gradient(135deg, ${color}15, white)`,
      border: `1px solid ${color}30`,
      borderRadius: { xs: 3, md: 4 },
      overflow: 'hidden',
      position: 'relative',
      cursor: 'pointer',
      height: '100%', // Ensure consistent height
    }} onClick={onClick}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 }, 
        position: 'relative', 
        zIndex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, md: 2 }, 
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap'
        }}>
          <Avatar className="action-icon" sx={{ 
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            width: { xs: 48, sm: 52, md: 56 },
            height: { xs: 48, sm: 52, md: 56 },
            boxShadow: `0 8px 25px ${color}40`,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 26, sm: 28, md: 30 }
            }
          }}>
            {icon}
          </Avatar>
          {badge && (
            <Chip 
              label={badge} 
              size="small" 
              sx={{ 
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: 'white',
                fontWeight: 600,
                height: { xs: '20px', md: '24px' },
                fontSize: { xs: '0.7rem', md: '0.75rem' }
              }}
            />
          )}
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, md: 1 }, 
            color: 'rgba(51,65,85,0.9)',
            lineHeight: 1.3,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(51,65,85,0.7)',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            display: { xs: '-webkit-box', md: 'block' },
            WebkitLineClamp: { xs: 2, md: 'none' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
);
};

// Fix PulsingDot to remove animation
const PulsingDot = ({ color, size = 8 }) => (
  <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <Box sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color
    }} />
  </Box>
);

// Add a loading indicator component
const LoadingIndicator = () => (
    <Box sx={{
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  }}>
    <CircularProgress color="primary" size={60} />
    <Typography variant="h6" sx={{ color: 'rgba(51,65,85,0.8)' }}>
      Chargement des données...
    </Typography>
  </Box>
);

// Completely redesigned Dashboard component to directly use real-time data from all sources
const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { settings } = useContext(SettingsContext);
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isXsMobile = useMediaQuery('(max-width:400px)');
  
  // Direct access to all data sources to ensure real-time updates
  const { transactions, stats: financialStats, loading: financialLoading } = useFinancialData();
  const { reservations, stats: reservationsStats, loading: reservationsLoading } = useReservationsData();
  const { clients, stats: clientsStats, loading: clientsLoading } = useClientsData();
  const { packages, stats: packagesStats, loading: packagesLoading } = usePackagesData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData();
  const { documents, loading: documentsLoading } = useDocumentsData();
  
  // Combined loading state
  const isLoading = financialLoading || reservationsLoading || clientsLoading || packagesLoading;
  
  // State for dashboard-specific data
  const [activeTab, setActiveTab] = useState(0);
  const [weatherData, setWeatherData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [error, setError] = useState(null);
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Log data for debugging
  console.log("Dashboard direct data:", { 
    financial: financialStats?.totalRevenue || 0,
    clients: clients?.length || 0,
    reservations: reservations?.length || 0,
    packages: packages?.length || 0
  });
    
  // Periodic refresh effect - forces a UI update every 5 seconds
  useEffect(() => {
    console.log('Dashboard: Setting up auto-refresh');
    
    // Function to force UI refresh
    const forceRefresh = () => {
      if (isMounted.current) {
        setLastRefresh(Date.now());
        console.log('Dashboard: Auto-refreshed at', new Date().toLocaleTimeString());
      }
    };
    
    // Set up interval for periodic refreshes (every 5 seconds)
    const refreshInterval = setInterval(forceRefresh, 5000);
    
    // Clean up on unmount
    return () => {
      isMounted.current = false;
      clearInterval(refreshInterval);
    };
  }, []);
  
  // Effect to handle component mount/unmount
  useEffect(() => {
    // Force initial data refresh on mount
    window.dispatchEvent(new CustomEvent('globalDataRefresh', {
      detail: { source: 'dashboard', requestType: 'full_refresh', timestamp: Date.now() }
    }));
    
    // Clean up function
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Show loading state while data is being fetched
  if (isLoading) {
    return <LoadingIndicator />;
  }
  
  // Show error state if there was a problem
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur de chargement des données: {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.dispatchEvent(new CustomEvent('globalDataRefresh', {
            detail: { type: 'error_retry', source: 'dashboard', timestamp: Date.now() }
          }))}
        >
          Réessayer
        </Button>
      </Box>
    );
  }
  
  // Create stats object directly from real-time data sources
  const stats = {
    // Client stats directly from clients data
    clients: clients ? clients.length : 0,
    
    // Reservation stats directly from reservations data
    reservations: reservations ? reservations.length : 0,
    
    // Financial data directly from financial stats
    revenue: financialStats ? (financialStats.totalRevenue || 0).toLocaleString() + ' DZD' : '0 DZD',
    
    // Fixed satisfaction for now (could be calculated from client feedback in a real system)
    satisfaction: '94.2%',
    
    // Generate recent activity from real transactions and reservations
    recentActivity: transactions && transactions.length > 0 ? 
      transactions.slice(0, 4).map((transaction, index) => ({
        id: transaction.id || index,
        type: transaction.type === 'Recette' ? 'payment' : 'alert',
        title: transaction.type === 'Recette' ? 'Paiement reçu' : 'Nouvelle dépense',
        description: transaction.description || `${transaction.montant?.toLocaleString()} DZD ${transaction.type === 'Recette' ? 'reçu' : 'payé'}`,
        time: '2 heures', // Would be calculated from actual timestamp
        status: transaction.type === 'Recette' ? 'success' : 'warning',
        icon: transaction.type === 'Recette' ? <AttachMoney /> : <Warning />,
      })) : [
      {
        id: 1,
        type: 'reservation',
        title: 'Nouvelle réservation',
        description: 'Ahmed Benali a réservé le forfait "Séjour à Paris"',
        time: '2 heures',
        status: 'success',
        icon: <Flight />,
      },
      {
        id: 2,
        type: 'payment',
        title: 'Paiement reçu',
        description: 'Paiement de 45,000 DZD reçu de Mohamed Kaci',
        time: '3 heures',
        status: 'success',
        icon: <AttachMoney />,
        }
      ],
    
    // Generate upcoming reservations from real reservation data
    upcomingReservations: reservations && reservations.length > 0 ?
      reservations
        .filter(res => new Date(res.dateDepart) > new Date())
        .sort((a, b) => new Date(a.dateDepart) - new Date(b.dateDepart))
        .slice(0, 3)
        .map(res => ({
          id: res.id,
          client: res.clientNom || 'Client',
          destination: res.destination || 'Destination',
          date: new Date(res.dateDepart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
          amount: `${(res.prixTotal || 0).toLocaleString()} DZD`,
          status: res.statut === 'Confirmée' ? 'confirmed' : 'pending',
          avatar: null,
        })) : [
      {
        id: 1,
        client: 'Lina Bouaziz',
        destination: 'Istanbul',
        date: '15 Août 2024',
        amount: '85,000 DZD',
        status: 'confirmed',
        avatar: null,
      },
      {
        id: 2,
        client: 'Yacine Rahmani',
        destination: 'Dubai',
        date: '22 Août 2024',
        amount: '120,000 DZD',
        status: 'pending',
        avatar: null,
        }
      ],
    
    // Generate top destinations from reservations data
    topDestinations: reservations && reservations.length > 0 ? 
      Array.from(
        reservations.reduce((acc, res) => {
          const destination = res.destination || 'Inconnue';
          if (!acc.has(destination)) {
            acc.set(destination, { count: 1, growth: '+5%' });
          } else {
            acc.set(destination, { 
              count: acc.get(destination).count + 1,
              growth: '+' + Math.floor(Math.random() * 20 + 1) + '%'
            });
          }
          return acc;
        }, new Map())
      )
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4) : [
      {
        name: 'Istanbul',
        count: 12,
        growth: '+18%',
      },
      {
        name: 'Dubai',
        count: 9,
        growth: '+12%',
        }
      ],
    
    // Generate chart data based on real data
    clientGrowth: clients && clients.length > 0 ? 
      [clients.length * 0.7, clients.length * 0.8, clients.length * 0.75, clients.length * 0.85, clients.length * 0.9, clients.length * 0.95, clients.length] : 
      [25, 30, 22, 28, 35, 40, 38],
    
    revenueGrowth: financialStats && financialStats.totalRevenue ? 
      [
        financialStats.totalRevenue * 0.6,
        financialStats.totalRevenue * 0.5,
        financialStats.totalRevenue * 0.7,
        financialStats.totalRevenue * 0.8,
        financialStats.totalRevenue * 0.7,
        financialStats.totalRevenue * 0.9,
        financialStats.totalRevenue
      ].map(val => Math.round(val / 10000)) : 
      [120, 90, 150, 180, 130, 170, 200],
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'success': return '#059669';
      case 'warning': return '#d97706';
      case 'error': return '#dc2626';
      case 'info': return '#0284c7';
      default: return '#6366f1';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'reservation': return <Flight sx={{ fontSize: 20 }} />;
      case 'payment': return <AttachMoney sx={{ fontSize: 20 }} />;
      case 'alert': return <Warning sx={{ fontSize: 20 }} />;
      case 'document': return <Description sx={{ fontSize: 20 }} />;
      default: return <Notifications sx={{ fontSize: 20 }} />;
    }
  };

  const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
          color: gradient ? 'white' : 'inherit',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          } : {},
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mb: 0.5, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' }
              }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ 
                  opacity: gradient ? 0.9 : 0.7, 
                  mt: 0.5,
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  label={trend} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: trend.startsWith('+') ? 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                    color: gradient ? 'white' : 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
                color: gradient ? 'white' : 'primary.main',
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 }
              }}
            >
              {icon}
            </Avatar>
          </Box>
          
          {progress && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  Progression
                </Typography>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradient ? 'white' : 'primary.main',
                  }
                }} 
              />
            </Box>
          )}
          
          {chart && (
            <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
              {chart}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );
  
  return (
    <Box sx={{ p: { xs: 0, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 800, 
          color: 'text.primary',
          mb: 1.5,
          fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Tableau de Bord Intelligent
        </Typography>
        <Typography variant="body1" sx={{ 
          color: 'text.secondary',
          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
        }}>
          Bienvenue Ahmed ! Voici votre vue d'ensemble en temps réel
        </Typography>
      </Box>
      
      {/* Quick Actions Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RocketLaunch sx={{ mr: 1.5, color: 'primary.main', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' } }}>
            Actions Rapides
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <FloatingActionCard 
              title="Nouveau Client"
              icon={<Add />}
              color="#6366f1"
              action="Rapide"
              description="Ajouter un client au système"
              onClick={() => navigate('/clients')}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FloatingActionCard 
              title="Nouvelle Réservation"
              icon={<Flight />}
              color="#10b981"
              action="Popular"
              description="Créer une nouvelle réservation"
              onClick={() => navigate('/reservations')}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FloatingActionCard 
              title="Nouveau Forfait"
              icon={<CardGiftcard />}
              color="#f59e0b"
              action="Trending"
              description="Créer un package de voyage"
              onClick={() => navigate('/forfaits')}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FloatingActionCard 
              title="Analytics"
              icon={<BarChart />}
              color="#8b5cf6"
              action="Pro"
              description="Analyser les performances"
              onClick={() => navigate('/rapports')}
            />
          </Grid>
        </Grid>
      </Box>
      
      {/* Key Metrics Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesome sx={{ mr: 1.5, color: 'primary.main', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' } }}>
            Métriques en Temps Réel
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <EnhancedStatCard
              title="Clients Actifs"
              value={stats.clients}
              icon={<People />}
              gradient={['#6366f1', '#8b5cf6']}
              subtitle="Base clients totale"
              trend="+12.5%"
              onClick={() => navigate('/clients')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <EnhancedStatCard
              title="Réservations"
              value={stats.reservations}
              icon={<Flight />}
              gradient={['#10b981', '#059669']}
              subtitle="Ce mois-ci"
              trend="+18.3%"
              onClick={() => navigate('/reservations')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <EnhancedStatCard
              title="Revenus"
              value={stats.revenue}
              icon={<AttachMoney />}
              gradient={['#f59e0b', '#d97706']}
              subtitle="Chiffre d'affaires"
              chart={<AnimatedChart data={stats.revenueGrowth} color="#ffffff" height={40} />}
              onClick={() => navigate('/finances')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <EnhancedStatCard
              title="Satisfaction"
              value={stats.satisfaction}
              icon={<Star />}
              gradient={['#8b5cf6', '#6d28d9']}
              subtitle="Note moyenne clients"
              trend="+3.2%"
              progress={94}
              onClick={() => navigate('/rapports')}
            />
          </Grid>
        </Grid>
      </Box>
      
      {/* Main Dashboard Content */}
      <Grid 
        container 
        spacing={{ xs: 2, md: 3 }} 
        sx={{ 
          px: { xs: 2, md: 0 },
          // Better spacing for mobile
          '& > .MuiGrid-item': {
            pb: { xs: 2, md: 3 }
          }
        }}
      >
        {/* Recent Activity */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: { xs: 2, md: 3 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            <CardContent sx={{ 
              p: { xs: 2, md: 3 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: { xs: 1.5, md: 2 }
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}>
                  Activité Récente
                </Typography>
                <Chip 
                  label="Aujourd'hui" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    height: { xs: '24px', md: '24px' },
                    '& .MuiChip-label': {
                      px: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.7rem', md: '0.75rem' }
                    }
                  }}
                />
              </Box>
              
              <List sx={{ p: 0 }}>
                {stats.recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        px: 0, 
                        py: 1.5,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Avatar
                          sx={{
                            bgcolor: `${getStatutColor(activity.status)}20`,
                            color: getStatutColor(activity.status),
                          }}
                        >
                          {getNotificationIcon(activity.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {activity.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Il y a {activity.time}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < stats.recentActivity.length - 1 && (
                      <Divider component="li" sx={{ ml: 5 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
              
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Voir toutes les activités
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Upcoming Reservations */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' } }}>Réservations à venir</Typography>
                <IconButton size="small">
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>
              
              <List sx={{ p: 0 }}>
                {stats.upcomingReservations.map((reservation, index) => (
                  <React.Fragment key={reservation.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        px: 0, 
                        py: 1.5,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          {reservation.avatar || reservation.client.charAt(0)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {reservation.client}
                            </Typography>
                            <Chip 
                              label={reservation.status === 'confirmed' ? 'Confirmé' : 'En attente'} 
                              size="small"
                              color={reservation.status === 'confirmed' ? 'success' : 'warning'}
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.65rem',
                                fontWeight: 500
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {reservation.destination}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Today sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {reservation.date}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {reservation.amount}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < stats.upcomingReservations.length - 1 && (
                      <Divider component="li" sx={{ ml: 5 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
              
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Gérer les réservations
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Top Destinations */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' } }}>Destinations Populaires</Typography>
                <Chip 
                  label="Ce mois" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
              
              <List sx={{ p: 0 }}>
                {stats.topDestinations.map((destination, index) => (
                  <ListItem 
                    key={destination.name}
                    sx={{ 
                      px: 0, 
                      py: 1.5,
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.02)',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 42 }}>
                      <Avatar sx={{ 
                        bgcolor: index === 0 ? '#fef3c7' : 
                                index === 1 ? '#e0e7ff' : 
                                index === 2 ? '#d1fae5' : '#f3e8ff',
                        color: index === 0 ? '#d97706' : 
                               index === 1 ? '#4f46e5' : 
                               index === 2 ? '#059669' : '#7e22ce',
                      }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {destination.name}
                          </Typography>
                          <Chip 
                            label={destination.growth} 
                            size="small"
                            color="success"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.65rem',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Group sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {destination.count} réservations
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              
              <Button 
                variant="outlined" 
                fullWidth 
                endIcon={<ArrowForward />}
                sx={{ 
                  mt: 2, 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Voir toutes les destinations
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 